/**
 * Carbon Savings Chart Component
 * 
 * Displays cumulative carbon savings over time compared to baseline.
 * Shows evidence-based CO2 reduction for different heating system options.
 */

import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area } from 'recharts';

// Local type definitions (to avoid dependency on @core)
export interface CarbonSavingsDataPoint {
  year: number;
  saving_kgCO2: number;
  cumulativeSaving_kgCO2: number;
  // Uncertainty bounds for confidence intervals
  uncertainty?: number;  // 0-1 scale (e.g., 0.15 = ±15%)
  lowBound_kgCO2?: number;
  highBound_kgCO2?: number;
}

export interface CarbonSavingsSeries {
  id: string;
  label: string;
  color: string;
  data: CarbonSavingsDataPoint[];
  totalSaving_tonnes: number;
  percentReduction: number;
  // Overall uncertainty for this series
  uncertainty?: number;  // 0-1 scale (e.g., 0.15 = ±15%)
  confidence?: 'high' | 'medium' | 'low';
}

export interface CarbonSavingsChartData {
  series: CarbonSavingsSeries[];
  metadata: {
    periodYears: number;
    yAxisLabel: string;
    xAxisLabel: string;
    unit: string;
  };
}

export interface CarbonSavingsChartProps {
  data: CarbonSavingsChartData;
  height?: number;
}

export function CarbonSavingsChart({ data, height = 400 }: CarbonSavingsChartProps) {
  // Transform data for Recharts format
  const chartData: Record<string, any>[] = [];
  
  // Find the maximum year from all series
  const maxYear = Math.max(
    ...data.series.flatMap((s: CarbonSavingsSeries) => s.data.map((d: CarbonSavingsDataPoint) => d.year))
  );
  
  // Build data points for each year
  for (let year = 0; year <= maxYear; year++) {
    const point: Record<string, any> = { year };
    
    data.series.forEach((series: CarbonSavingsSeries) => {
      const yearData = series.data.find((d: CarbonSavingsDataPoint) => d.year === year);
      if (yearData) {
        // Keep as number for Recharts, will format in tooltip/legend
        point[series.id] = yearData.cumulativeSaving_kgCO2 / 1000;
        
        // Add error bar bounds if available
        if (yearData.lowBound_kgCO2 !== undefined && yearData.highBound_kgCO2 !== undefined) {
          point[`${series.id}_low`] = yearData.lowBound_kgCO2 / 1000;
          point[`${series.id}_high`] = yearData.highBound_kgCO2 / 1000;
        }
      }
    });
    
    chartData.push(point);
  }
  
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis 
            dataKey="year" 
            label={{ value: data.metadata.xAxisLabel, position: 'insideBottom', offset: -5 }}
            stroke="var(--color-text-secondary)"
          />
          <YAxis 
            label={{ value: data.metadata.yAxisLabel, angle: -90, position: 'insideLeft' }}
            stroke="var(--color-text-secondary)"
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: 'var(--color-bg)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
            }}
            formatter={(value: any) => [`${Number(value).toFixed(2)} tonnes CO₂`, '']}
          />
          <Legend 
            wrapperStyle={{
              paddingTop: '20px'
            }}
            formatter={(value: string) => {
              const series = data.series.find((s: CarbonSavingsSeries) => s.id === value);
              if (series) {
                return `${series.label} (${series.totalSaving_tonnes.toFixed(1)}t, ${series.percentReduction.toFixed(0)}% reduction)`;
              }
              return value;
            }}
          />
          
          {data.series.map((series: CarbonSavingsSeries) => {
            const hasErrorBars = series.data[0]?.lowBound_kgCO2 !== undefined;
            
            return (
              <React.Fragment key={series.id}>
                {/* Error bar area (confidence interval) - rendered as shaded band */}
                {hasErrorBars && (
                  <>
                    <Area
                      type="monotone"
                      dataKey={`${series.id}_high`}
                      stroke="none"
                      fill={series.color}
                      fillOpacity={0.15}
                      legendType="none"
                      stackId={series.id}
                    />
                    <Area
                      type="monotone"
                      dataKey={`${series.id}_low`}
                      stroke="none"
                      fill="var(--color-bg, white)"
                      fillOpacity={1}
                      legendType="none"
                      stackId={series.id}
                    />
                  </>
                )}
                
                {/* Main line */}
                <Line
                  type="monotone"
                  dataKey={series.id}
                  stroke={series.color}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </React.Fragment>
            );
          })}
        </LineChart>
      </ResponsiveContainer>
      
      {/* Summary cards below chart */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 'var(--spacing-md)',
        marginTop: 'var(--spacing-lg)'
      }}>
        {data.series.map((series: CarbonSavingsSeries) => (
          <div
            key={series.id}
            style={{
              padding: 'var(--spacing-md)',
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              borderLeft: `4px solid ${series.color}`,
            }}
          >
            <h4 style={{ 
              margin: 0, 
              marginBottom: 'var(--spacing-xs)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-secondary)'
            }}>
              {series.label}
            </h4>
            <div style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'bold',
              color: series.color,
              marginBottom: 'var(--spacing-xs)'
            }}>
              {series.totalSaving_tonnes.toFixed(1)} tonnes
            </div>
            <div style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-secondary)'
            }}>
              {series.percentReduction.toFixed(0)}% reduction over {data.metadata.periodYears} years
            </div>
          </div>
        ))}
      </div>
      
      {/* Technical note on variance (only show if any series has uncertainty data) */}
      {data.series.some(s => s.data.some(d => d.uncertainty !== undefined)) && (
        <div style={{
          marginTop: 'var(--spacing-lg)',
          padding: 'var(--spacing-md)',
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
        }}>
          <h4 style={{ 
            margin: 0, 
            marginBottom: 'var(--spacing-xs)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'bold',
            color: 'var(--color-text-primary)'
          }}>
            Technical Note on Variance
          </h4>
          <p style={{ margin: 0, lineHeight: '1.5' }}>
            The shaded ranges displayed represent the sensitivity of the physical model to variables such as 
            system sludge, installation heat-loss, and external ambient temperatures. The central value represents 
            the most probable outcome based on ISO/SEDBUK standards. Wider ranges indicate greater uncertainty 
            in the model inputs (e.g., estimated sludge in aging baseline systems), while narrower ranges indicate 
            higher-quality evidence (e.g., tightly regulated ErP ratings for modern systems).
          </p>
        </div>
      )}
    </div>
  );
}

export default CarbonSavingsChart;
